import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useAuth } from '../context/AuthContext';

// ── Step 1: Info screen (org logo, exam details) ──────────────────────────────
// ── Step 2: Register or login
// ── Step 3: Opt-in / Opt-out with reason (inline, not a separate page)
// ── Step 4: Done

export default function ExamRegistration() {
  const { token }         = useParams();
  const navigate          = useNavigate();
  const { login, user }   = useAuth();

  const [linkInfo,    setLinkInfo]    = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [submitting,  setSubmitting]  = useState(false);
  const [mode,        setMode]        = useState('register'); // 'register' | 'login'
  const [step,        setStep]        = useState('auth');    // 'auth' | 'opt'
  const [optStatus,   setOptStatus]   = useState('in');      // 'in' | 'out'
  const [optReason,   setOptReason]   = useState('');
  const [examId,      setExamId]      = useState(null);
  const [alreadyEnrolled, setAlreadyEnrolled] = useState(false);

  const [form,      setForm]      = useState({ full_name:'', email:'', password:'', confirm:'' });
  const [loginForm, setLoginForm] = useState({ email:'', password:'' });

  useEffect(() => {
    // If already logged in as student, skip to opt step
    if (user?.role === 'student') {
      setStep('opt');
    }
    axios.get(`/api/auth/register-link/${token}`)
      .then(r => { setLinkInfo(r.data); setExamId(r.data.exam_id); })
      .catch(() => setLinkInfo(null))
      .finally(() => setPageLoading(false));
  }, [token, user]);

  // ── Register new account ──────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    if (form.password !== form.confirm) return toast.error('Passwords do not match');
    if (form.password.length < 6) return toast.error('Password must be at least 6 characters');
    setSubmitting(true);
    try {
      const res = await axios.post('/api/auth/register-via-link', {
        email: form.email, password: form.password, full_name: form.full_name, token,
      });
      sessionStorage.setItem('token', res.data.token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;
      setExamId(res.data.exam_id);
      setStep('opt'); // Go to opt-in step
    } catch (err) {
      if (err.response?.data?.error === 'already_registered') {
        // Auto-switch to login tab with message
        toast.info('You are already registered. Please login to manage your enrollment.');
        setLoginForm(p => ({ ...p, email: form.email }));
        setMode('login');
      } else {
        toast.error(err.response?.data?.error || 'Registration failed');
      }
    } finally { setSubmitting(false); }
  };

  // ── Login existing account ────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const loggedInUser = await login(loginForm.email, loginForm.password);
      if (loggedInUser?.role !== 'student') {
        navigate('/dashboard');
        return;
      }
      // Try to enroll via link
      try {
        const res = await axios.post('/api/auth/register-via-link', {
          email: loginForm.email, password: loginForm.password, full_name: '', token,
        });
        setExamId(res.data.exam_id || linkInfo?.exam_id);
      } catch (enrollErr) {
        // Check if already enrolled
        const errMsg = enrollErr.response?.data?.error;
        if (errMsg === 'already_registered') {
          setAlreadyEnrolled(true);
        }
        setExamId(linkInfo?.exam_id);
      }
      setStep('opt');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Login failed');
    } finally { setSubmitting(false); }
  };

  // ── Submit opt-in / opt-out ───────────────────────────────────────────────────
  const handleOptSubmit = async () => {
    if (optStatus === 'out' && !optReason.trim()) {
      toast.error('Please provide a reason for opting out');
      return;
    }
    setSubmitting(true);
    try {
      const eid = examId || linkInfo?.exam_id;
      await axios.post(`/api/organizations/exams/${eid}/opt`, {
        opt_status: optStatus,
        opt_reason: optStatus === 'out' ? optReason : null,
      });
      if (optStatus === 'in') {
        toast.success('✅ You are enrolled for this exam!');
      } else {
        toast.success('You have opted out. You can opt back in anytime via this link.');
      }
      navigate('/dashboard');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save preference');
    } finally { setSubmitting(false); }
  };

  if (pageLoading) return <div className="loading-screen"><div className="spinner" /></div>;

  if (!linkInfo) return (
    <div className="auth-page">
      <div className="auth-card" style={{ textAlign:'center', maxWidth:420 }}>
        <div style={{ fontSize:48, marginBottom:16 }}>🔗</div>
        <h2 style={{ fontSize:22, fontWeight:700, marginBottom:12 }}>Invalid Link</h2>
        <p style={{ color:'var(--text-secondary)', marginBottom:24 }}>
          This registration link is invalid, expired, or no longer active.
        </p>
        <Link to="/login" className="btn btn-primary" style={{ justifyContent:'center' }}>
          Go to Login →
        </Link>
      </div>
    </div>
  );

  return (
    <div className="auth-page">
      <div className="auth-card" style={{ maxWidth:520 }}>

        {/* Org + Exam banner */}
        <div style={{ textAlign:'center', marginBottom:24 }}>
          {linkInfo.logo_url && (
            <img src={linkInfo.logo_url} alt="org logo"
              style={{ height:48, borderRadius:10, objectFit:'contain', marginBottom:10 }} />
          )}
          <div style={{ fontSize:13, color:'var(--text-muted)', fontFamily:'var(--font-mono)', marginBottom:4 }}>
            {linkInfo.org_name}
          </div>
          <div style={{ fontSize:20, fontWeight:700, marginBottom:8 }}>{linkInfo.exam_title}</div>
          <div style={{ display:'flex', justifyContent:'center', gap:20, fontSize:13, color:'var(--text-secondary)' }}>
            <span>⏱ {linkInfo.duration_minutes} min</span>
            <span>🎯 {linkInfo.total_marks} marks</span>
          </div>
        </div>

        {/* ── STEP: AUTH ──────────────────────────────────────────────── */}
        {step === 'auth' && (
          <>
            {/* Mode toggle */}
            <div style={{ display:'flex', gap:8, marginBottom:20 }}>
              {[['register','New Student'],['login','Already Registered']].map(([m,l]) => (
                <button key={m} onClick={() => setMode(m)}
                  className="btn btn-secondary"
                  style={{ flex:1, justifyContent:'center',
                    ...(mode===m ? { borderColor:'var(--accent-primary)', color:'var(--accent-primary)', background:'rgba(79,142,247,0.1)' } : {}) }}>
                  {l}
                </button>
              ))}
            </div>

            {mode === 'register' && (
              <form onSubmit={handleRegister}>
                <div className="form-group">
                  <label className="form-label">Full Name *</label>
                  <input className="form-input" value={form.full_name}
                    onChange={e => setForm(p => ({...p, full_name:e.target.value}))}
                    placeholder="Your full name" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Email Address *</label>
                  <input className="form-input" type="email" value={form.email}
                    onChange={e => setForm(p => ({...p, email:e.target.value}))}
                    placeholder="your@email.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password *</label>
                  <input className="form-input" type="password" value={form.password}
                    onChange={e => setForm(p => ({...p, password:e.target.value}))}
                    placeholder="Minimum 6 characters" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password *</label>
                  <input className="form-input" type="password" value={form.confirm}
                    onChange={e => setForm(p => ({...p, confirm:e.target.value}))}
                    placeholder="Repeat password" required />
                </div>
                <button className="btn btn-primary w-full" type="submit" disabled={submitting}
                  style={{ justifyContent:'center' }}>
                  {submitting ? 'Creating account...' : 'Register & Continue →'}
                </button>
              </form>
            )}

            {mode === 'login' && (
              <form onSubmit={handleLogin}>
                <div className="alert alert-info" style={{ marginBottom:16, fontSize:13 }}>
                  Already have an account? Log in to manage your enrollment for this exam.
                </div>
                <div className="form-group">
                  <label className="form-label">Email</label>
                  <input className="form-input" type="email" value={loginForm.email}
                    onChange={e => setLoginForm(p => ({...p, email:e.target.value}))}
                    placeholder="your@email.com" required />
                </div>
                <div className="form-group">
                  <label className="form-label">Password</label>
                  <input className="form-input" type="password" value={loginForm.password}
                    onChange={e => setLoginForm(p => ({...p, password:e.target.value}))}
                    placeholder="••••••••" required />
                </div>
                <button className="btn btn-primary w-full" type="submit" disabled={submitting}
                  style={{ justifyContent:'center' }}>
                  {submitting ? 'Logging in...' : 'Login & Continue →'}
                </button>
              </form>
            )}
          </>
        )}

        {/* ── STEP: OPT-IN / OPT-OUT ─────────────────────────────────── */}
        {step === 'opt' && (
          <div>
            <div style={{ textAlign:'center', marginBottom:20 }}>
              <div style={{ fontSize:32, marginBottom:8 }}>📋</div>
              <h3 style={{ fontSize:18, fontWeight:700, marginBottom:6 }}>Confirm Your Enrollment</h3>
              {alreadyEnrolled ? (
                <div className="alert alert-info" style={{ fontSize:13 }}>
                  You are already enrolled in <strong>{linkInfo.exam_title}</strong>.
                  You can change your participation preference below.
                </div>
              ) : (
                <p style={{ fontSize:13, color:'var(--text-secondary)' }}>
                  You have been enrolled in <strong>{linkInfo.exam_title}</strong>.
                  Do you want to participate?
                </p>
              )}
            </div>

            {/* Opt choice cards */}
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12, marginBottom:20 }}>
              {/* Opt In */}
              <div onClick={() => setOptStatus('in')} style={{
                padding:'16px 14px', borderRadius:10, cursor:'pointer', textAlign:'center',
                border:`2px solid ${optStatus==='in' ? 'var(--accent-success)' : 'var(--border)'}`,
                background: optStatus==='in' ? 'rgba(16,185,129,0.08)' : 'var(--bg-elevated)',
                transition:'all 0.15s',
              }}>
                <div style={{ fontSize:28, marginBottom:6 }}>✅</div>
                <div style={{ fontWeight:700, fontSize:14, color: optStatus==='in' ? 'var(--accent-success)' : 'var(--text-primary)' }}>
                  Opt In
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                  I want to take this exam
                </div>
              </div>
              {/* Opt Out */}
              <div onClick={() => setOptStatus('out')} style={{
                padding:'16px 14px', borderRadius:10, cursor:'pointer', textAlign:'center',
                border:`2px solid ${optStatus==='out' ? 'var(--accent-danger)' : 'var(--border)'}`,
                background: optStatus==='out' ? 'rgba(239,68,68,0.08)' : 'var(--bg-elevated)',
                transition:'all 0.15s',
              }}>
                <div style={{ fontSize:28, marginBottom:6 }}>❌</div>
                <div style={{ fontWeight:700, fontSize:14, color: optStatus==='out' ? 'var(--accent-danger)' : 'var(--text-primary)' }}>
                  Opt Out
                </div>
                <div style={{ fontSize:11, color:'var(--text-muted)', marginTop:4 }}>
                  I will not take this exam
                </div>
              </div>
            </div>

            {/* Opt-out reason */}
            {optStatus === 'out' && (
              <div className="form-group">
                <label className="form-label">Reason for opting out *</label>
                <textarea className="form-input form-textarea" style={{ minHeight:80 }}
                  value={optReason}
                  onChange={e => setOptReason(e.target.value)}
                  placeholder="Please explain why you are not taking this exam..." />
              </div>
            )}

            <button
              className={`btn w-full ${optStatus === 'in' ? 'btn-primary' : 'btn-danger'}`}
              style={{ justifyContent:'center' }}
              onClick={handleOptSubmit}
              disabled={submitting || (optStatus === 'out' && !optReason.trim())}
            >
              {submitting ? 'Saving...' : optStatus === 'in' ? '✅ Confirm Enrollment →' : '❌ Opt Out & Proceed'}
            </button>

            <div style={{ textAlign:'center', marginTop:14, fontSize:12, color:'var(--text-muted)' }}>
              You can change your preference anytime before the exam starts.
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
